/** Client CRM — entreprise d'assurance / mutuelle. */
export interface Insurer {
  id: string;
  code: string;
  name: string;
  /** N° agrément ou identification réglementaire. */
  licenseNumber: string;
  phone: string;
  email: string;
  address: string;
  contactPerson: string;
  active: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface InsurerInput {
  code: string;
  name: string;
  licenseNumber?: string;
  phone?: string;
  email?: string;
  address?: string;
  contactPerson?: string;
  active?: boolean;
  notes?: string;
}

/** Agent assureur — référent prise en charge. */
export interface InsurerAgent {
  id: string;
  insurerId: string;
  ref: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InsurerAgentInput {
  insurerId: string;
  ref: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  active?: boolean;
}

export type CoverageCaseStatus = "pending" | "approved" | "rejected" | "partial";

export type CoverageCaseKind =
  | "consultation"
  | "prescription"
  | "hospitalization"
  | "laboratory"
  | "imaging";

/** Dossier de prise en charge — visible côté hôpital et portail assureur. */
export interface CoverageCase {
  id: string;
  insurerId: string;
  agentId: string | null;
  patientId: string;
  admissionId: string | null;
  prescriptionId: string | null;
  labOrderId: string | null;
  imagingExamId: string | null;
  billingDocumentId: string | null;
  kind: CoverageCaseKind;
  status: CoverageCaseStatus;
  /** Libellé acte / motif. */
  label: string;
  amount: number;
  memberNumber: string;
  policyNumber: string;
  rejectionReason: string;
  reviewedAt: string | null;
  reviewedBy: string;
  createdAt: string;
  updatedAt: string;
  /** Soumission envoyée au hub central assureur. */
  hubSubmissionId: string | null;
  hubSubmittedAt: string | null;
}

export interface CoverageCaseInput {
  insurerId: string;
  agentId?: string | null;
  patientId: string;
  admissionId?: string | null;
  prescriptionId?: string | null;
  labOrderId?: string | null;
  imagingExamId?: string | null;
  billingDocumentId?: string | null;
  kind: CoverageCaseKind;
  label: string;
  amount?: number;
  memberNumber?: string;
  policyNumber?: string;
}

export const COVERAGE_CASE_STATUS_LABELS: Record<CoverageCaseStatus, string> = {
  pending: "En attente assureur",
  approved: "Prise en charge validée",
  rejected: "Refusée",
  partial: "Prise en charge partielle",
};

export const COVERAGE_CASE_KIND_LABELS: Record<CoverageCaseKind, string> = {
  consultation: "Consultation",
  prescription: "Ordonnance / pharmacie",
  hospitalization: "Hospitalisation",
  laboratory: "Laboratoire",
  imaging: "Imagerie",
};

export function insurerAgentFullName(
  agent: Pick<InsurerAgent, "firstName" | "lastName">,
): string {
  return `${agent.lastName} ${agent.firstName}`.trim();
}
